import { Link } from 'react-router-dom';
import {
  Button,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { IconDatabaseOff } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import { DOCUMENT_TYPE_LABEL } from '#/service/driver-management/constants/documentTypes.ts';
import { GENDER_OPTIONS } from '#/service/driver-management/constants/fieldOptions.ts';
import { LICENSE_CLASS_LABEL } from '#/service/driver-management/constants/licenseClasses.ts';
import {
  useApprovedDrivingLicense,
  useApprovedVehicleRegistration,
  useLatestAccountDocument,
} from '../hooks/useAccountDocuments.ts';
import { SectionState } from './SectionState.tsx';

type IdentityDocumentType = 'DRIVING_LICENSE' | 'VEHICLE_REGISTRATION';

const SHORT_LABEL: Record<IdentityDocumentType, string> = {
  DRIVING_LICENSE: '駕照',
  VEHICLE_REGISTRATION: '行照',
};

const reviewPath = (documentId: string) =>
  `/driver-management/document-review/${documentId}`;

const DrivingLicenseFields = ({ documentId }: { documentId: string }) => {
  const { data, error, isPending } = useApprovedDrivingLicense(documentId);

  if (isPending) return <SectionState status="loading" />;
  if (error) return <SectionState status="error" error={error} />;

  const gender =
    GENDER_OPTIONS.find(({ value }) => value === data.gender)?.label ??
    data.gender;

  return (
    <Stack gap="md">
      <SimpleGrid cols={3}>
        <div>
          <Text size="sm" c="dimmed">
            姓名
          </Text>
          <Text mt={4}>{data.name}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed">
            駕照種類
          </Text>
          <Text mt={4}>{LICENSE_CLASS_LABEL[data.licenseClass]}</Text>
        </div>
      </SimpleGrid>
      <Divider />
      <SimpleGrid cols={3}>
        <div>
          <Text size="sm" c="dimmed">
            性別
          </Text>
          <Text mt={4}>{gender}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed">
            身分證字號
          </Text>
          <Text mt={4}>{data.nationalId}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed">
            出生年月日
          </Text>
          <Text mt={4}>{format(parseISO(data.birthDate), 'yyyy/MM/dd')}</Text>
        </div>
      </SimpleGrid>
      <Divider />
      <div>
        <Text size="sm" c="dimmed">
          地址
        </Text>
        <Text mt={4}>{data.address}</Text>
      </div>
    </Stack>
  );
};

const VehicleRegistrationFields = ({ documentId }: { documentId: string }) => {
  const { data, error, isPending } = useApprovedVehicleRegistration(documentId);

  if (isPending) return <SectionState status="loading" />;
  if (error) return <SectionState status="error" error={error} />;

  return (
    <Stack gap="md">
      <SimpleGrid cols={3}>
        <div>
          <Text size="sm" c="dimmed">
            姓名
          </Text>
          <Text mt={4}>{data.ownerName}</Text>
        </div>
        <div>
          <Text size="sm" c="dimmed">
            車牌號碼
          </Text>
          <Text mt={4}>{data.plateNumber}</Text>
        </div>
      </SimpleGrid>
      <Divider />
      <div>
        <Text size="sm" c="dimmed">
          地址
        </Text>
        <Text mt={4}>{data.ownerAddress}</Text>
      </div>
    </Stack>
  );
};

type DocumentCardProps = {
  accountId: string;
  documentType: IdentityDocumentType;
};

const DocumentCard = ({ accountId, documentType }: DocumentCardProps) => {
  const {
    data: document,
    error,
    isPending,
  } = useLatestAccountDocument(accountId, documentType);

  const label = DOCUMENT_TYPE_LABEL[documentType];
  const shortLabel = SHORT_LABEL[documentType];

  const renderContent = () => {
    if (isPending) return <SectionState status="loading" />;
    if (error) return <SectionState status="error" error={error} />;

    if (!document || document.reviewState === 'SUBMITTED') {
      return <SectionState status="empty" message={`用戶未上傳${label}`} />;
    }

    switch (document.reviewState) {
      case 'PENDING_REVIEW':
        return (
          <Stack align="center" gap="xs" py="md">
            <ThemeIcon variant="light" color="gray" radius="xl" size="lg">
              <IconDatabaseOff size={18} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              用戶已上傳{label}，尚未審核完成
            </Text>
            <Button
              variant="light"
              component={Link}
              to={reviewPath(document.id)}
            >
              審核{shortLabel}
            </Button>
          </Stack>
        );

      case 'REJECTED':
        return (
          <Stack align="center" gap="xs" py="md">
            <ThemeIcon variant="light" color="gray" radius="xl" size="lg">
              <IconDatabaseOff size={18} />
            </ThemeIcon>
            <Text size="sm" c="dimmed" ta="center">
              用戶已上傳{label}，審核失敗
              <br />
              退件原因：{document.rejectReason}
            </Text>
            <Button
              variant="light"
              component={Link}
              to={reviewPath(document.id)}
            >
              查看{shortLabel}
            </Button>
          </Stack>
        );

      case 'APPROVED':
        return documentType === 'DRIVING_LICENSE' ? (
          <DrivingLicenseFields documentId={document.id} />
        ) : (
          <VehicleRegistrationFields documentId={document.id} />
        );
    }
  };

  return (
    <Paper radius="lg" p="xl" shadow="xs" h="100%">
      <Group justify="space-between" mb="md" mih={36}>
        <Title order={4}>{label}</Title>
        {document?.reviewState === 'APPROVED' && (
          <Button
            variant="outline"
            component={Link}
            to={reviewPath(document.id)}
          >
            查看{shortLabel}
          </Button>
        )}
      </Group>
      {renderContent()}
    </Paper>
  );
};

export { DocumentCard };
